import type { ReactNode } from 'react'
import { Kicker, Titulo, Regua, Lede, type TituloVariant } from './type'
import { Rastro, type Source } from './Metric'
import type { State } from './tokens'

/**
 * Casca da anatomia (cap. 09; ADR-014/015).
 *  <Page state="construcao">  → emite data-state-dominant; cada Dobra declara a superfície (D25: herói tinta, seções alternam).
 *    <Dobra hero surface="dark" kicker title lede aside …>  → trilho à esquerda + campo/aside à direita; herói assenta a 40% do vão.
 *    <Dobra stack kicker title lede>…</Dobra>                → pilha (modelo do Ângelo): título de seção em cima, campo em toda a largura.
 *    <Dobra>…</Dobra>                                        → dobra corrida (só campo).
 *
 * Contratos que viram erro de compilação/execução: dobra hero sem kicker, title ou lede lança em dev (a anatomia não é opcional).
 */
type Surface = 'dark' | 'light'
const cx = (...c: Array<string | false | undefined>) => c.filter(Boolean).join(' ')

export interface DobraProps {
  /** Dobra de abertura: min-height e colocação a 40% do vão residual. */
  hero?: boolean
  kicker?: ReactNode
  title?: ReactNode
  titleVariant?: TituloVariant
  lede?: ReactNode
  /** Rastro (fonte) — obrigatório sempre que o campo trouxer métrica; o gate inverso cobra. */
  rastro?: Source
  /** Conteúdo do campo. */
  children?: ReactNode
  /** Escapes controlados: elemento à direita do trilho no herói (o laço), e superfície forçada (só o <Page> usa). */
  aside?: ReactNode
  surface?: Surface
  /** Layout "pilha" (modelo do Ângelo, D25): trilho em cima (título de seção 32px + lede) e campo em toda a largura embaixo. */
  stack?: boolean
  id?: string
  className?: string
}

export function Dobra({ hero, kicker, title, titleVariant, lede, rastro, children, aside, surface = 'light', stack, id, className }: DobraProps) {
  if (import.meta.env.DEV && hero && (!kicker || !title || !lede)) {
    throw new Error('Dobra hero exige kicker, title e lede — a anatomia não é opcional (Manual v1.0, p.082)')
  }
  const hasTrilho = Boolean(kicker || title)
  const body = (
    <div className={cx('z-shell', stack && 'z-shell--stack')}>
      {hasTrilho && (
        <div className="z-trilho">
          {kicker && <Kicker>{kicker}</Kicker>}
          {title && <Titulo variant={titleVariant ?? (hero ? 'editorial' : 'h2')} as={hero ? 'h1' : 'h2'}>{title}</Titulo>}
          <Regua />
          {lede && <Lede>{lede}</Lede>}
        </div>
      )}
      {/* O campo é anatomia sempre que carrega conteúdo — com ou sem trilho; herói vazio não conta (o gate exige ≥ 1 campo por página). */}
      <div className={cx('z-campo', !hasTrilho && 'z-campo--full')} data-anatomy={children || aside || rastro ? 'campo' : undefined}>
        {children}
        {rastro && <Rastro source={rastro} />}
        {aside}
      </div>
    </div>
  )
  return (
    <section id={id} className={cx('z-dobra', hero && 'z-dobra--hero', className)} data-surface={surface}>
      {hero ? (<><div className="z-dobra__vao-antes" />{body}<div className="z-dobra__vao-depois" /></>) : body}
    </section>
  )
}

export interface PageProps {
  /** Um estado dominante por rota (docs/state-map.md). */
  state: State
  children: ReactNode
}

/**
 * Page — carimba o estado dominante da rota. Chão (D25, Jean 2026-09-06, modelo do Ângelo = exibição de site do manual):
 * o herói é tinta e a página ALTERNA o chão por seção — cada Dobra declara `surface`; o gate `anatomy.spec.ts` cobra
 * herói em tinta, papel presente e no máximo 3 dobras seguidas na mesma superfície. (D23, chão de papel, valeu 04–06/09.)
 */
export function Page({ state, children }: PageProps) {
  return <div data-state-dominant={state} data-state={state}>{children}</div>
}

/** Campo em colunas: 1, 2 ou 3 — nunca 4 (tipo). */
export function Field({ columns = 1, children, className }: { columns?: 1 | 2 | 3; children: ReactNode; className?: string }) {
  return <div className={cx('z-field', columns > 1 && `z-field--${columns}`, className)}>{children}</div>
}
